import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BellRing, X, Plus, Save } from "lucide-react";

export const SyncAlertSettings = () => {
  const { data } = useQuery({ queryKey: ["drchrono-alert-settings"], queryFn: async () => (await api.get("/drchrono/alert-settings")).data });
  const [recips, setRecips] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  useEffect(() => { if (data && !recips) setRecips(data.recipients); }, [data, recips]);

  const save = useMutation({
    mutationFn: async () => (await api.post("/drchrono/alert-settings", { recipients: recips })).data,
    onSuccess: (d) => { setRecips(d.recipients); toast.success("Alert recipients saved"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"),
  });
  const add = () => {
    const e = newEmail.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(e)) { toast.error("Enter a valid email"); return; }
    if (!recips.includes(e)) setRecips([...recips, e]);
    setNewEmail("");
  };
  if (!recips) return null;

  return (
    <div data-testid="sync-alert-settings-card" className="bg-white border border-slate-200 rounded-xl shadow-sm mt-6 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center gap-2">
        <BellRing className="h-4 w-4 text-cyan-500" />
        <h3 className="font-heading font-bold text-slate-900">Sync Alert Recipients</h3>
        <span className="text-xs text-slate-400">· emailed when sync fails twice in a row, and when it recovers</span>
      </div>
      <div className="p-6 space-y-4">
        <div className="flex flex-wrap gap-2" data-testid="sync-alert-recipients">
          {recips.map((r) => (
            <span key={r} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
              {r}
              <button data-testid={`sync-alert-remove-${r}`} onClick={() => setRecips(recips.filter((x) => x !== r))}
                className="text-slate-400 hover:text-red-600"><X className="h-3 w-3" /></button>
            </span>
          ))}
          {recips.length === 0 && <span className="text-xs text-slate-400">No recipients — add at least one.</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Input data-testid="sync-alert-email-input" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder="Add recipient email" className="h-9 max-w-xs" />
          <Button data-testid="sync-alert-add" variant="outline" className="h-9" onClick={add}><Plus className="h-4 w-4 mr-1.5" />Add</Button>
          <Button data-testid="sync-alert-save" className="h-9 bg-slate-900 hover:bg-slate-800 text-white"
            disabled={save.isPending || recips.length === 0} onClick={() => save.mutate()}>
            <Save className="h-4 w-4 mr-1.5" />Save
          </Button>
        </div>
      </div>
    </div>
  );
};
